import Product, { ProductModel } from '~/models/product';

import { Brand, Category, ColorInfo, ParametricData, PriceInfo, ProductRaw } from '~/constants/types/algolia';
import { ProductVariantModel } from '~/models/productVariant';

import { DirectiveBinding } from 'vue';
import { getLocalCurrency } from '~/composeables/useLocalizedCurrency';
import { useCartStore } from '~/store/cart';
import { useProductStore } from '~/store/product';
import { CartApiItem, OrderProduct } from '~/constants/types/norce';
import useStatefulCookie from '~/composeables/useStatefulCookie';
import { NorceInfoTypesCodes } from '~/constants/norceCodes';
import {useGlobalContentStore} from "~/store/globalContent";

type VtrackInput = {
  product: ProductRaw,
  pageName: string,
  pageId: number,
  index: number,
}

export type DataLayerProduct = {
  index?: number;
  item_brand?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category?: string;
  item_id: string;
  item_list_id?: string; // - sidans id kristian
  item_list_name?: string; // - sidans namn
  item_name: string;
  item_variant?: string; // - färg
  price?: number;
  quantity?: number,
  discount?: number,
  coupon?: string,
}
export type DataLayerInput = {
  brand: Brand,
  categories: Category[],
  color?: ParametricData,
  colorInfo?: ColorInfo,
  name: string,
  partNo: string,
  price: PriceInfo,
}

export const useProductImpressionTracking = {
  mounted: (el: HTMLElement, input: DirectiveBinding<VtrackInput>)  => {
    const productStore = useProductStore();
    if (input.value.product) {
      const observer = new IntersectionObserver(
        (entries, observer) => {

          const productItem = Product.create(input.value.product);
          entries.forEach(entry => {
            const item = createDataLayerItem(productItem, 1, input.value.index);
            if (input.value.pageName) {
              item['item_list_name'] = input.value.pageName;
              item['item_list_id'] = input.value.pageId + '';
            }
            if (entry.intersectionRatio > 0 && window.dataLayer) {
              productStore.batchTrack(item);
              observer.unobserve(entry.target);
            }
          });

        }, {
          threshold: .5, // Wait til we se at least half the observed
        });
      observer.observe(el);
    } else { console.warn('Used tracking directive without value'); }
  },
};

/**
 * This replaces direct usage of window.datalayer.push,
 * adds algolia tracking id
 * algoliaUserToken: 'user-1',
 */
export const pushToDataLayer = (data: object) => {
  const browserTrackingCookie = useStatefulCookie('randomId', { maxAge: 3600 * 24 * 365 });
  const algoliaUserToken = browserTrackingCookie.value || '';
  data.algoliaUserToken = algoliaUserToken;

  if (window.dataLayer) {
    window.dataLayer.push(data);
  } else {
    console.warn('attempted useTracking pushDataLayer without datalayer');
  }
};

export const sendDataLayer = (
  event: string,
  items: DataLayerProduct[],
  additionalData: object = {}
) => {
  // Clear previous ecommerce data to prevent GTM from merging events
  if (window.dataLayer) {
    window.dataLayer.push({ ecommerce: null });
  }

  pushToDataLayer({
    event,
    ecommerce: {
      ...additionalData,
      currency: getLocalCurrency(),
      items,
    },
  });
};

type AdditionalCartData = {
  value?: string | number;
  coupon?: string;
  payment_type?: string;
  shipping_tier?: string;
  created_by?: string;
}

type ListInfo = {
  item_list_id?: string;
  item_list_name?: string;
}

/**
 * Uses the cartstore to create an event that includes items in cart
 * @param event
 * @param additionalData
 * @param listInfo
 */
export const createEventFromCart = (event: string, additionalData: AdditionalCartData = {}, listInfo?: ListInfo) => {
  const cartStore = useCartStore();
  const trackProducts = [] as DataLayerProduct[];
  if (!additionalData.value && cartStore.cart.summary?.subtotalBeforeVat) {
    additionalData.value = Number(cartStore.cart.summary.subtotalBeforeVat.replace(/\s/g, '').replace(',', ''));
  }
  if (!additionalData.coupon && cartStore.cart.appliedPromotions?.length) {
    additionalData.coupon = cartStore.cart.appliedPromotions[0]?.discountCode ?? cartStore.cart.appliedPromotions[0]?.name;
  }
  cartStore.cart.items.forEach((item, index) => {
    const dlItem = createDataLayerItem(item, item.quantity, index + 1);
    if (listInfo?.item_list_id) dlItem.item_list_id = listInfo.item_list_id;
    if (listInfo?.item_list_name) dlItem.item_list_name = listInfo.item_list_name;
    trackProducts.push(dlItem);
  });
  sendDataLayer(event, trackProducts, additionalData);
};

/**
 * Converts to DataLayerProduct
 */
export const createDataLayerItem = (
  product: ProductModel | ProductRaw | CartApiItem | OrderProduct | ProductVariantModel,
  quantity = 1,
  index = 1,
  brand?: string,
  categories?: any,
  includeListInformation?: boolean
): DataLayerProduct => {
  let coupon = null;
  if (product.appliedPromotions?.length) {
    coupon = product.appliedPromotions[0]?.discountCode ?? product.appliedPromotions[0].name;
  }

  let discount = null;
  if (product.priceInfo?.priceBeforeVat !== product.priceInfo.priceBeforeDiscountBeforeVat) {
    discount = Number(product.priceInfo?.priceBeforeDiscountBeforeVat?.replace(/\s/g, '').replace(',', '')) - Number(product.priceInfo?.priceBeforeVat?.replace(/\s/g, '').replace(',', ''));
  }

  let name = product.name;
  let variantName = product.variantName ?? product.name;

  let dataLayer = null;
  if (product.infoTypes?.length) {
    dataLayer = JSON.parse(product.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.DataLayer)[0]?.value ?? 'null');
  } else if (product.info?.length) {
    dataLayer = JSON.parse(product.info?.filter((i) => i.name === NorceInfoTypesCodes.DataLayer)[0]?.value ?? 'null');
  }
  if (dataLayer) {
    name = dataLayer.item_name;
    variantName = dataLayer.item_variant;
  }

  const output = {
    item_name: name,
    item_id: product.partNo,
    item_variant: variantName,
    price: Number(product.priceInfo.priceBeforeVat?.replace(/\s/g, '').replace(',', '')),
    discount,
    quantity,
    index,
    coupon,
    item_brand: product.brand?.name || (brand ?? ''),
  } as DataLayerProduct;

  if (!categories && product.categories?.length) {
    categories = product.categories;
  }
  categories?.map((i)=> i.name).slice(0,4).forEach((c, index)=> {
    // @ts-ignore
    output[`item_category${index > 0 ? index + 1:''}`] = c;
  });

  if (includeListInformation) {
    const globalContent = useGlobalContentStore();
    const listContext = globalContent.trackingListContext;
    output['item_list_name'] = listContext?.name;
    output['item_list_id'] = listContext?.id;
  }

  return output;
};
